<template>
  <v-container
    fluid
    class="grey lighten-4">
    <!-- Header Summary -->
    <v-row
      dense
      class="mb-4">
      <v-col cols="12">
        <v-card
          flat
          class="rounded-lg pa-4 d-flex align-center">
          <v-icon
            color="primary"
            large
            left>
            mdi-account-group
          </v-icon>
          <div>
            <div class="text-h5 font-weight-black">
              {{ onlineEmployees.length }} / {{ allEmployeesToday.length }}
            </div>
            <div class="text-caption grey--text">
              พนักงานที่กำลังปฏิบัติงาน / ทั้งหมดวันนี้
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-chip
            color="success"
            label
            outlined
            font-weight-bold>
            <v-icon
              left
              x-small>
              mdi-circle
            </v-icon> LIVE UPDATES
          </v-chip>
        </v-card>
      </v-col>
    </v-row>

    <!-- Group by Department -->
    <div
      v-for="(employees, dept) in groupedByDepartment"
      :key="dept"
      class="mb-6">
      <div class="d-flex align-center mb-3">
        <div class="text-subtitle-1 font-weight-bold primary--text">
          {{ dept }}
        </div>
        <v-divider class="ml-4"></v-divider>
        <v-chip
          x-small
          class="ml-2">
          {{ employees.length }} คน
        </v-chip>
      </div>

      <v-row dense>
        <v-col
          v-for="emp in employees"
          :key="emp.id"
          cols="12"
          md="4">
          <div
            rounded="lg"
            elevation="1"
            :class="getCSSNotWorkingStatus(emp.notWorkingStatus).card"
            class="pa-4 employee--card">
            <v-row
              no-gutters
              align="center">
              <!-- 1. Status Dot อยู่ซ้ายสุด และกึ่งกลางแนวตั้ง -->
              <v-col
                cols="auto"
                class="mr-4">
                <div
                  :class="emp.isWorking ? 'teal--text text--lighten-3' : 'grey--text text--lighten-2'"
                  style="font-size: 24px; line-height: 1;"
                >
                  ●
                </div>
              </v-col>

              <v-col>
                <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">
                  {{ emp.firstName }} {{ emp.lastName }}
                </div>
                <div class="grey--text">
                  {{ emp.employeeCode }}
                </div>
              </v-col>

              <v-col
                cols="auto"
                class="pl-4">
                <div v-if="emp.isWorking">
                  <div
                    class="d-flex align-center justify-end blue-grey--text text--lighten-1"
                  >
                    <v-icon
                      small
                      color="blue-grey lighten-1"
                      class="mr-1">
                      mdi-briefcase
                    </v-icon>
                    <span class="text-body-2">{{ emp.currentTask.name }}</span>
                  </div>
                  <div
                    class="success--text d-flex align-center justify-end font-weight-bold text-h6"
                  >
                    {{ emp.liveTimer }}
                  </div>
                </div>

                <div v-else>
                  <div
                    class="d-flex align-center justify-end">
                    <v-icon
                      small
                      color="blue-grey lighten-1"
                      class="mr-1">
                      mdi-briefcase-remove
                    </v-icon>
                    <span class="text-body-2">ว่าง</span>
                  </div>
                  <div
                    :class="getCSSNotWorkingStatus(emp.notWorkingStatus).text"
                    class="d-flex align-center justify-end font-weight-bold text-h6"
                  >
                    {{ emp.liveTimer }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import WarehouseWorkTrackingProvider from '@/resources/WarehouseWorkTrackingProvider'

const provider = new WarehouseWorkTrackingProvider()

export default {
  data: () => ({
    allEmployeesToday: [],
    timerInterval: null,
    refreshInterval: null
  }),

  computed: {
    groupedByDepartment () {
      const groups = {}
      this.allEmployeesToday.forEach((emp) => {
        const dept = emp.department || 'ไม่ระบุแผนก'
        if (!groups[dept]) groups[dept] = []
        groups[dept].push(emp)
      })
      return groups
    },
    onlineEmployees () {
      return this.allEmployeesToday.filter((e) => e.isWorking)
    }
  },

  mounted () {
    this.fetchEmployeeStatus()
    this.timerInterval = setInterval(this.updateLiveTimers, 1000)
    // Auto-refresh ข้อมูลจาก API ทุก 30 วินาที
    this.refreshInterval = setInterval(this.fetchEmployeeStatus, 30000)
  },

  beforeDestroy () {
    clearInterval(this.timerInterval)
    clearInterval(this.refreshInterval)
  },

  methods: {
    getCSSNotWorkingStatus (status) {
      if (status === 'red-status') {
        return {
          text: 'red--text',
          card: 'card--not--working--red'
        }
      }
      if (status === 'orange-status') {
        return {
          text: 'orange--text',
          card: 'card--not--working--orange'
        }
      }

      return {
        text: '',
        card: 'card--working'
      }
    },
    async fetchEmployeeStatus () {
      try {
        const result = await provider.getEmployeeStatus()
        const statusData = result.data || []
        this.allEmployeesToday = statusData.map((item) => ({
          id: item.employee?.id,
          employeeCode: item.employee?.employeeCode,
          firstName: item.employee?.firstName,
          lastName: item.employee?.lastName,
          department: item.employee?.department,
          isWorking: item.isWorking,
          lastActivityDate: new Date(item.employee?.lastActivityDate),
          currentTask: item.currentTask
            ? {
                name: item.currentTask.taskType?.name || '-',
                startAt: new Date(item.currentTask.startAt)
              }
            : null,
          notWorkingStatus: '',
          liveTimer: ''
        }))
        // คำนวณ timer ทันทีหลัง fetch
        this.updateLiveTimers()
      } catch (err) {
        console.error('Failed to fetch employee status:', err)
      }
    },
    updateLiveTimers () {
      this.allEmployeesToday.forEach((emp, index) => {
        if (emp.isWorking && emp.currentTask?.startAt) {
          const diff = Math.floor((new Date() - emp.currentTask.startAt) / 1000)
          const h = Math.floor(diff / 3600).toString().padStart(2, '0')
          const m = Math.floor((diff % 3600) / 60).toString().padStart(2, '0')
          const s = (diff % 60).toString().padStart(2, '0')
          this.$set(this.allEmployeesToday, index, {
            ...emp,
            notWorkingStatus: '',
            liveTimer: `${h}:${m}:${s}`
          })
        } else {
          const diff = Math.floor((new Date() - emp.lastActivityDate) / 1000)
          const h = Math.floor(diff / 3600).toString().padStart(2, '0')
          const m = Math.floor((diff % 3600) / 60).toString().padStart(2, '0')
          const s = (diff % 60).toString().padStart(2, '0')

          let notWorkingStatus = ''

          // more than 10 min
          if (diff > 600) {
            notWorkingStatus = 'red-status'
          } else {
            notWorkingStatus = 'orange-status'
          }

          this.$set(this.allEmployeesToday, index, {
            ...emp,
            notWorkingStatus,
            liveTimer: `${h}:${m}:${s}`
          })
        }
      })
    }
  }
}
</script>

<style scoped>
.employee--card {
  border-radius: 6px;
  border: 1px solid #cfcfcf;
  background-color: #fff;
}
.employee--card {
  transition: transform 0.2s;
}
.employee--card:hover {
  transform: translateY(-2px);
}
.card--working {
  border: 1px solid #80cbc4;
}
.card--not--working--red {
  background-color: #fbf0f0;
  border: 1px solid #F44336;
}

.card--not--working--orange {
  background-color: rgb(255, 240, 240);
  border: 1px solid #ff9800;
}
</style>
